import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useMatch, useNavigate, useParams } from 'react-router-dom';
import { TopBar } from '../shell/TopBar';
import { PrimaryNav, type PrimarySelection } from '../shell/PrimaryNav';
import { SecondaryNav } from '../shell/SecondaryNav';
import { usePlatform } from '../data/queries/usePlatform';
import { MockRibbon } from '../components/honesty';
import { moduleDataStatus } from '../config/systems';
import { AuthTokenSync } from '../auth/AuthTokenSync';
import { useAuth } from '../auth/useAuth';
import { canAccessAdmin, filterSuitesForClaims } from '../auth/permissions';

export function App() {
  const { data } = usePlatform();
  const { claims } = useAuth();
  const [primaryOpen, setPrimaryOpen] = useState(true);
  const [secondaryOpen, setSecondaryOpen] = useState(true);

  const navigate = useNavigate();
  const params = useParams<{ systemId?: string; moduleId?: string }>();
  const onHub = useMatch('/');
  const onAdmin = useMatch('/admin');
  const onSystem = useMatch('/system/:systemId');
  const onModule = useMatch('/system/:systemId/:moduleId');

  // Filter the platform suites by the active user's role. Users with access
  // to a single system land directly inside it (skipping the multi-system Hub).
  const visibleSuites = useMemo(
    () => (data ? filterSuitesForClaims(data.suites, claims) : []),
    [data, claims],
  );
  const adminVisible = canAccessAdmin(claims);

  // Scroll to top on route change, mirroring the prototype's behaviour.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [params.systemId, params.moduleId, onHub, onAdmin]);

  if (!data) {
    return (
      <div className="platform" data-primary="open" data-secondary="none">
        <AuthTokenSync />
        <TopBar />
      </div>
    );
  }

  const inHub = Boolean(onHub);
  const inAdmin = Boolean(onAdmin);
  const inSystem = Boolean(onSystem || onModule);
  const system = inSystem
    ? data.suites.find((s) => s.id === params.systemId) ?? null
    : null;

  // Permission gates:
  //   - Non-admins cannot reach /admin → bounce to first allowed system.
  //   - Users with exactly one allowed system see that system instead of the
  //     multi-system Hub on `/`.
  //   - Users hitting a system they don't have access to are bounced too.
  if (inAdmin && !adminVisible) {
    const fallback = visibleSuites[0]?.id ?? '';
    return <Navigate to={fallback ? `/system/${fallback}` : '/'} replace />;
  }
  if (inHub && visibleSuites.length === 1) {
    return <Navigate to={`/system/${visibleSuites[0]!.id}`} replace />;
  }
  if (inSystem && system && !visibleSuites.some((s) => s.id === system.id)) {
    const fallback = visibleSuites[0]?.id ?? '';
    return <Navigate to={fallback ? `/system/${fallback}` : '/'} replace />;
  }

  const activeSelection: PrimarySelection = inHub
    ? 'hub'
    : inAdmin
      ? 'admin'
      : system?.id ?? 'hub';

  // Default to the first module when only the system is in the URL.
  const activeModuleId = system
    ? params.moduleId ?? system.modules[0]?.id ?? null
    : null;

  // Show the diagonal mock ribbon whenever the active module is not yet
  // wired to a live backend. Hub/admin views are always "live" shell.
  const showMockRibbon =
    inSystem && system && activeModuleId
      ? moduleDataStatus(system.id, activeModuleId) === 'mock'
      : false;

  return (
    <div
      className="platform"
      data-primary={primaryOpen ? 'open' : 'closed'}
      data-secondary={(inHub || inAdmin) ? 'none' : (secondaryOpen ? 'open' : 'closed')}
    >
      <AuthTokenSync />
      <TopBar />
      <PrimaryNav
        suites={visibleSuites}
        pinned={data.pinned}
        alerts={data.alerts}
        activeSelection={activeSelection}
        adminVisible={adminVisible}
        open={primaryOpen}
        toggle={() => setPrimaryOpen((o) => !o)}
      />
      {system && !inHub && !inAdmin && (
        <SecondaryNav
          system={system}
          activeModuleId={activeModuleId}
          open={secondaryOpen}
          toggle={() => setSecondaryOpen((o) => !o)}
        />
      )}
      <main className="platform-main">
        <Outlet context={{ system, activeModuleId, navigate }} />
      </main>
      {showMockRibbon && <MockRibbon />}
    </div>
  );
}
